import axios from 'axios'

import { refreshKeycloakToken } from '@/main'
import { useAppStore } from '@/store/app'
import { useAuthStore } from '@/store/auth'
import { useToast } from '@/store/toast'

import {
  FE_ERROR_GPT_ACCESS_REVOKED,
  FE_ERROR_GPT_NOT_READY,
  GPT_ACCESS_REVOKED_USER_MESSAGE,
  GPT_NOT_READY_USER_MESSAGE,
  NOT_AUTHENTICATED_DETAIL,
  SESSION_EXPIRED_USER_MESSAGE,
  TOKEN_EXPIRED_DETAIL,
} from './feErrorCodes'

import type { AxiosError, AxiosResponse, CreateAxiosDefaults } from 'axios'

export * from './modules'

export interface IPaginatedResponse<T> {
  items: T[]
  page: number
  pages: number
  size: number
  total: number
}

export interface IPaginationParams {
  /**
   * Page number
   * @default 1
   */
  page?: number
  /**
   * Number of items per page (1-100)
   * @default 10
   */
  size?: number
  /**
   * Skip auto commit.
   */
}

// Function to create a new Axios instance with interceptors
export const createApiClient = (axiosConfigs: CreateAxiosDefaults) => {
  const apiClient = axios.create(axiosConfigs)

  // Request interceptor to add the Authorization header
  apiClient.interceptors.request.use(async (config) => {
    await refreshKeycloakToken()

    config.headers['Authorization'] = `Bearer ${useAuthStore().accessToken}`
    return config
  })

  // Response interceptor for error handling
  apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (!axios.isAxiosError(error)) {
        return Promise.reject(error)
      }
      if (isNoAccessError(error)) {
        if (isIdentityRequest(error)) {
          useAppStore().setIsUserAuthorisedForEverGpt(false)
        }
        return Promise.reject(error)
      }
      // Website-password errors (`/w/{slug}`) are 401/403 but unrelated to the
      // Keycloak session — retrying after a token refresh only duplicates the
      // request and delays surfacing the password prompt to the user.
      if (isWebsiteAuthBusinessError(error)) {
        return Promise.reject(error)
      }
      const originalRequest = error.config
      if (!originalRequest) {
        return Promise.reject(error)
      }
      const configWithRetry = originalRequest as typeof originalRequest & {
        _retry?: boolean
      }
      if (isAuthenticationError(error) && !configWithRetry._retry) {
        configWithRetry._retry = true
        // A 401 means the token itself was rejected, whatever the adapter thinks of it.
        await refreshKeycloakToken({ force: error.response?.status === 401 })
        configWithRetry.headers['Authorization'] = `Bearer ${
          useAuthStore().accessToken
        }`
        return apiClient(configWithRetry)
      }

      return Promise.reject(error)
    },
  )

  return apiClient
}

const isAuthenticationError = (error: AxiosError): boolean => {
  const { response } = error
  if (!response) return false

  const isUnauthorized = response.status === 401
  const isNotAuthenticated = response.status === 403
  return isUnauthorized || isNotAuthenticated
}

/** The identity endpoint — the app's single source of truth for EverGPT access. */
const IDENTITY_REQUEST_PATH = '/users/me'

/**
 * True for the `GET /users/me` request, the ONLY answer to "may this user use
 * EverGPT at all".
 *
 * `isNoAccessError` matches a generic 403 body that the backend also returns for a
 * *resource* the user simply isn't allowed to touch — most visibly `GET /gpts/{id}`
 * for a GPT that was never shared with them, which is exactly where the public GPT
 * page sends a full EverGPT user (`is_authorized` means "not a PENDING user", not
 * "may open this GPT"). Flipping the app-wide flag on that answer logged a
 * perfectly valid user out onto /not-authorised. Scoping it here means a
 * per-resource 403 stays a per-resource error, and access is re-evaluated on the
 * next identity call — one bootstrap later at worst.
 */
const isIdentityRequest = (error: AxiosError): boolean => {
  const url = error.config?.url
  if (!url) return false
  return url.split('?')[0].replace(/\/+$/, '').endsWith(IDENTITY_REQUEST_PATH)
}

/**
 * If true then the user is denied access to EverGPT by planet9 service
 * @param error AxiosError
 * @returns boolean
 */
export const isNoAccessError = (error: AxiosError): boolean => {
  const { response } = error
  if (!response) return false

  const responseData = response.data as { detail: string }
  return (
    response.status === 403 &&
    responseData.detail === 'User is not authorized to access this resource'
  )
}

const WEBSITE_AUTH_BUSINESS_ERROR_DETAILS = new Set([
  'password_required',
  'invalid_password',
])

/**
 * True for the `/w/{slug}` website-password errors (e.g.
 * `{ detail: 'password_required', error_code: 'auth_error' }`). These share
 * the 401/403 status with real Keycloak auth failures but are a distinct,
 * non-retryable business error — retrying after a token refresh would just
 * send a duplicate request that fails the same way.
 */
const isWebsiteAuthBusinessError = (error: AxiosError): boolean => {
  const { response } = error
  if (!response) return false
  if (
    response.status !== 400 &&
    response.status !== 401 &&
    response.status !== 403
  ) {
    return false
  }

  let data: unknown = response.data
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data)
    } catch {
      return false
    }
  }
  if (!data || typeof data !== 'object') return false

  const detail = (data as { detail?: unknown }).detail
  return (
    typeof detail === 'string' &&
    WEBSITE_AUTH_BUSINESS_ERROR_DETAILS.has(detail)
  )
}

export const apiClientVersioned = createApiClient({
  baseURL: import.meta.env.VITE_API_ENDPOINT,
})
export const apiClient = createApiClient({
  baseURL: import.meta.env.VITE_API_ENDPOINT,
  headers: {
    'Content-Type': 'application/json',
  },
})
export const apiClientNoPrepend = createApiClient({
  baseURL: import.meta.env.VITE_API_ENDPOINT.replace('/api/v1', ''),
  headers: {
    'Content-Type': 'application/json',
  },
})

// Client for downloading files
export const fileUploadClient = createApiClient({
  baseURL: import.meta.env.VITE_API_ENDPOINT,
  headers: {
    'Content-Type': 'multipart/form-data',
  },
})

export const bufferApiClient = createApiClient({
  baseURL: import.meta.env.VITE_API_ENDPOINT.replace('/api', ''), // Removing prefix since it already exists in the file.url property
  responseType: 'arraybuffer',
})

/**
 * Anonymous-first client for the public GPT share page (`/public/gpts/...`). The
 * backend serves these endpoints without a JWT (`get_optional_public_user`), so a
 * visitor needs no Foundever account to open a shared GPT.
 *
 * Unlike every `createApiClient` instance it does NOT call `refreshKeycloakToken()`
 * per request: that redirects the browser to the Keycloak login screen when a stale
 * refresh token fails to renew, which is the exact bounce this page must never do.
 * The bearer is attached ONLY when one already exists, so the backend can still
 * recognise a signed-in user and answer `is_authorized` (what lights up "Open this
 * GPT"); anonymous visitors send no `Authorization` header at all.
 *
 * No response interceptor either — the shared one flips
 * `setIsUserAuthorisedForEverGpt(false)` on a 403, which is wrong for a page that
 * intentionally serves visitors without EverGPT access. So 404/410/429 surface as a
 * raw AxiosError for the page to map to user-facing copy.
 */
export const apiClientPublic = axios.create({
  baseURL: import.meta.env.VITE_API_ENDPOINT,
  headers: {
    'Content-Type': 'application/json',
  },
})
apiClientPublic.interceptors.request.use((config) => {
  const token = useAuthStore().accessToken
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`
  }
  return config
})

export const returnData = async <T>(req: Promise<AxiosResponse<T>>) =>
  req
    .then(({ data }) => data)
    .catch((err: AxiosError<unknown>) => {
      throw err
    })
export interface IServerError {
  detail: string
}
/**
 * Asserts whether given error is `ServerError`
 * @param error
 * @returns boolean
 */
export const isServerError = (
  error: unknown,
): error is AxiosError<{ detail: string }> => {
  return (
    axios.isAxiosError(error) &&
    error.response &&
    error.response.data &&
    error.response.data.detail &&
    typeof error.response.data.detail === 'string'
  )
}
/**
 * Asserts whether given error is a 409 — the request lost to state that already
 * exists server-side (e.g. a duplicate). Retrying it unchanged always fails: the
 * caller has to reconcile with the server first.
 */
export const isConflictError = (
  error: unknown,
): error is AxiosError<{ detail?: string }> =>
  axios.isAxiosError(error) && error.response?.status === 409

/**
 * Helper function to extract the custom API request error message
 */
type FeErrorBody = {
  detail?: string
  error_code?: string
  fe_error_code?: string
  error_message?: string
}

/**
 * The backend's own error code for an API failure, so a caller can react to a
 * specific one instead of matching on message text.
 *
 * An HTTP error body carries a single `error_code`: the backend fills it with the
 * raised error's FE-specific code when it sets one, and otherwise with a generic
 * class for the status (`bad_request`, `auth_error`, …). So reading `error_code`
 * first is what surfaces a specific code — there is no separate `fe_error_code`
 * key on an HTTP body. That spelling is the stream shape (see `useEventStream`),
 * kept here as a fallback for bodies that carry it.
 */
export const apiErrorCode = (error: unknown): string | undefined => {
  if (!axios.isAxiosError(error)) return undefined
  const data = error.response?.data as FeErrorBody | undefined
  if (!data || typeof data !== 'object') return undefined
  return data.error_code ?? data.fe_error_code
}

const feErrorFromAxios = (error: AxiosError<unknown>): string | undefined => {
  const data = error.response?.data as FeErrorBody | undefined
  if (!data || typeof data !== 'object') return undefined
  if (data.detail === TOKEN_EXPIRED_DETAIL) return SESSION_EXPIRED_USER_MESSAGE
  if (data.fe_error_code === FE_ERROR_GPT_NOT_READY) {
    return data.error_message || data.detail || GPT_NOT_READY_USER_MESSAGE
  }
  if (data.fe_error_code === FE_ERROR_GPT_ACCESS_REVOKED) {
    return data.error_message || data.detail || GPT_ACCESS_REVOKED_USER_MESSAGE
  }
  return undefined
}

/** PATCH chat (e.g. rename) when the assistant is no longer accessible to the user. */
export const isGptAccessRevokedAxiosError = (
  error: unknown,
): error is AxiosError<unknown> => {
  if (!axios.isAxiosError(error)) return false
  if (error.response?.status !== 403) return false
  const data = error.response?.data as FeErrorBody | undefined
  if (!data || typeof data !== 'object') return false
  if (data.fe_error_code === FE_ERROR_GPT_ACCESS_REVOKED) return true
  if (
    typeof data.detail === 'string' &&
    data.detail === GPT_ACCESS_REVOKED_USER_MESSAGE
  ) {
    return true
  }
  return false
}

export const getErrorMessage = (error: AxiosError<unknown>) => {
  const fe = feErrorFromAxios(error)
  if (fe) return fe
  if (isServerError(error)) {
    return error.response?.data.detail
  } else if (error.message) {
    return error.message
  }

  return 'Something went wrong.'
}

/**
 * Helper to handle API errors
 * Note: replace this with actual error handling (i.e. Sentry)
 */
export const handleApiError = (error: AxiosError<unknown>) => {
  console.log('[API error]:', getErrorMessage(error))
}
/**
 * Helper to handle API errors and show a toast
 * Note: replace this with actual error handling (i.e. Sentry)
 */
export const handleApiErrorToast = (error: AxiosError<unknown>) => {
  const errorMessage = getErrorMessage(error) || 'API Error'
  if (errorMessage === NOT_AUTHENTICATED_DETAIL) return Promise.reject()
  useToast().onError(errorMessage)
  return Promise.reject(error)
}

/**
 * Normalizes a stream error payload (e.g. SSE event: error with JSON data) into an Error.
 * Supports payloads with `error_message` and falls back to Error instance, string, or default message.
 */
export type StreamErrorPayload = {
  error_message?: string
  /** Some SSE error payloads use `message` instead of `error_message`. */
  message?: string
  code?: string
}

export const normalizeStreamError = (err: unknown): Error => {
  const payload =
    typeof err === 'object' && err !== null ? (err as StreamErrorPayload) : {}
  const code = typeof payload.code === 'string' ? payload.code : undefined
  const payloadText =
    typeof payload.error_message === 'string'
      ? payload.error_message
      : typeof payload.message === 'string'
        ? payload.message
        : undefined
  let errorMessage =
    payloadText ??
    (err instanceof Error
      ? err.message
      : typeof err === 'string'
        ? err
        : 'Stream error occurred')
  if (code === FE_ERROR_GPT_NOT_READY) {
    errorMessage = payloadText?.trim() || GPT_NOT_READY_USER_MESSAGE
  }
  if (code === FE_ERROR_GPT_ACCESS_REVOKED) {
    errorMessage = payloadText?.trim() || GPT_ACCESS_REVOKED_USER_MESSAGE
  }
  if (err instanceof Error) {
    err.message = errorMessage
    ;(err as Error & { streamErrorCode?: string }).streamErrorCode = code
    return err
  }
  const out = new Error(errorMessage)
  ;(out as Error & { streamErrorCode?: string }).streamErrorCode = code
  return out
}
